import { projectPatientStatus } from "./patientProjection";

const confirmedWrite = (result) =>
  result === true || (typeof result === "number" && result > 0);

const currentScope = (isCurrent) => {
  try {
    return typeof isCurrent !== "function" || isCurrent();
  } catch (error) {
    return false;
  }
};

export const createPatientStatusAdapter = ({
  projectPatient = projectPatientStatus,
  writePatient,
  republish,
} = {}) => {
  if (typeof projectPatient !== "function") {
    throw new TypeError("projectPatient must be a function");
  }
  if (typeof writePatient !== "function") {
    throw new TypeError("writePatient must be a function");
  }
  if (typeof republish !== "function") {
    throw new TypeError("republish must be a function");
  }
  const writesByMrn = new Map();
  const queuedPatientsByMrn = new Map();
  const retainedMrns = new Set();
  let active = true;

  const apply = ({
    patient,
    getPatient,
    event,
    algorithms,
    isCurrent,
  } = {}) => {
    if (!active || !currentScope(isCurrent)) {
      return Promise.resolve({ outcome: "ignored" });
    }
    let initialPatient;
    try {
      initialPatient =
        typeof getPatient === "function" ? getPatient() : patient;
    } catch (error) {
      return Promise.resolve({ outcome: "fallback" });
    }
    const mrn = initialPatient?.mrn;
    if (mrn === undefined || mrn === null || mrn === "") {
      return Promise.resolve({ outcome: "fallback" });
    }

    const execute = async () => {
      if (!active || !currentScope(isCurrent)) return { outcome: "ignored" };
      let activePatient;
      try {
        activePatient =
          queuedPatientsByMrn.get(mrn) ||
          (typeof getPatient === "function"
            ? getPatient()
            : patient);
      } catch (error) {
        return { outcome: "fallback" };
      }
      if (!activePatient || activePatient.mrn !== mrn) {
        return { outcome: "ignored" };
      }

      let projection;
      try {
        projection = projectPatient(activePatient, event, algorithms);
      } catch (error) {
        return { outcome: "fallback" };
      }
      if (projection?.outcome !== "applied") {
        return projection?.outcome === "ignored"
          ? { outcome: "ignored" }
          : { outcome: "fallback" };
      }
      if (!active || !currentScope(isCurrent)) return { outcome: "ignored" };

      let writeResult;
      try {
        writeResult = await writePatient(projection.patient);
      } catch (error) {
        return { outcome: "fallback" };
      }
      if (!confirmedWrite(writeResult)) return { outcome: "fallback" };
      if (!active) return { outcome: "ignored" };
      queuedPatientsByMrn.set(mrn, projection.patient);
      if (!currentScope(isCurrent)) {
        retainedMrns.add(mrn);
        return { outcome: "ignored" };
      }

      try {
        await republish({ isCurrent });
      } catch (error) {
        if (!active) return { outcome: "ignored" };
        retainedMrns.add(mrn);
        return { outcome: "fallback" };
      }
      if (!active || !currentScope(isCurrent)) {
        if (!active) return { outcome: "ignored" };
        retainedMrns.add(mrn);
        return { outcome: "ignored" };
      }
      retainedMrns.delete(mrn);
      return { outcome: "applied", patient: projection.patient };
    };

    const previous = writesByMrn.get(mrn) || Promise.resolve();
    const operation = previous.catch((error) => { console.warn("patientStatusAdapter.js queue continuation suppressed previous failure", error); }).then(execute);
    writesByMrn.set(mrn, operation);
    const clear = () => {
      if (writesByMrn.get(mrn) === operation) {
        writesByMrn.delete(mrn);
        if (!retainedMrns.has(mrn)) queuedPatientsByMrn.delete(mrn);
      }
    };
    operation.then(clear, clear);
    return operation;
  };

  const cleanup = () => {
    active = false;
    writesByMrn.clear();
    queuedPatientsByMrn.clear();
    retainedMrns.clear();
  };

  return { apply, cleanup };
};

export default createPatientStatusAdapter;
